/**
 * notifier.ts — how the engine tells an app something worth saying, without
 * knowing that app's toast component or its language.
 *
 * WHY THIS EXISTS. `useSparkly` used to do `import('@core/services/notify')`
 * and pass it a Spanish sentence. That was fine while the engine lived inside
 * the kids app and was the only front. It is not fine now: `packages/` must not
 * depend on an app (dependencies point one way), and the two apps do not even
 * speak the same language to the student — kids speaks Spanish, the student
 * front is English by Rafael's explicit rule.
 *
 * So the engine reports the FACT and each app chooses the words. An app that
 * registers nothing gets the no-op default: a missing hint is a missing hint,
 * never a crash and never a wrong-language string.
 */

/** The facts the engine can report. Add a case here, then handle it in each app. */
export type SparklyHint =
    /** The student recorded a long, rambling turn (>7s or >25 kB). Not blocking —
     *  the audio is still sent; shorter turns just get better answers. */
    | 'long-audio';

export interface SparklyNotifier {
    hint(kind: SparklyHint): void;
}

const NO_OP: SparklyNotifier = { hint: () => { } };

let current: SparklyNotifier = NO_OP;

/** Called once at app boot. `apps/legacy/app-main.ts` is the precedent. */
export function setSparklyNotifier(notifier: SparklyNotifier): void {
    current = notifier;
}

/** Used by the engine. Never throws, never blocks. */
export function sparklyNotifier(): SparklyNotifier {
    return current;
}
